import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
} from "../../app/queries/notifications";
import { NetworkErrorAlertContext } from "../../context/NetworkErrorAlert";
import { getNotificationViewPath } from "./notificationNavigation";
import { NotificationItem } from "./types";
import { useNotificationReceipt } from "./useNotificationReceipt";

type Options = {
  onBeforeNavigate?: () => void;
};

export const useNotificationActions = (options: Options = {}) => {
  const navigate = useNavigate();
  const { onShowAlert } = useContext(NetworkErrorAlertContext);
  const markNotificationRead = useMarkNotificationRead();
  const markAllNotificationsRead = useMarkAllNotificationsRead();
  const {
    receiptOpen,
    receipt,
    receiptError,
    receiptLoading,
    downloadReceipt,
    closeReceipt,
  } = useNotificationReceipt();
  const [showMarkedToast, setShowMarkedToast] = useState(false);

  useEffect(() => {
    if (!showMarkedToast) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setShowMarkedToast(false);
    }, 2500);

    return () => window.clearTimeout(timeoutId);
  }, [showMarkedToast]);

  const markAsReadIfNeeded = (notification: NotificationItem) => {
    if (!notification.read) {
      markNotificationRead.mutate(Number(notification.id));
    }
  };

  const handleMarkAllAsRead = async (unreadCount: number) => {
    if (unreadCount === 0 || markAllNotificationsRead.isLoading) {
      return;
    }

    await markAllNotificationsRead.mutateAsync();
    setShowMarkedToast(true);
  };

  const handleNotificationClick = (notification: NotificationItem) => {
    if (notification.read) {
      return;
    }

    markNotificationRead.mutate(Number(notification.id));
  };

  const handleViewTransaction = (notification: NotificationItem) => {
    markAsReadIfNeeded(notification);

    const path = getNotificationViewPath(notification);
    if (!path) {
      onShowAlert?.(null, "No destination available for this notification.");
      return;
    }

    options.onBeforeNavigate?.();
    navigate(path);
  };

  const handleDownloadReceipt = (notification: NotificationItem) => {
    markAsReadIfNeeded(notification);
    downloadReceipt(notification);
  };

  return {
    showMarkedToast,
    isMarkingAllAsRead: markAllNotificationsRead.isLoading,
    handleMarkAllAsRead,
    handleNotificationClick,
    handleViewTransaction,
    handleDownloadReceipt,
    receiptOpen,
    receipt,
    receiptError,
    receiptLoading,
    closeReceipt,
  };
};
